import { withAlpha } from "@/lib/color";
// AUTO-ESTRATTO da TeamDetail.jsx (refactor strutturale Fase 10.01) — nessun cambio di logica/UI/testid.
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import PlayerAvatar from "@/components/PlayerAvatar";
import { flagUrl } from "@/lib/flag";
import { trPositionShort } from "@/lib/pesTranslations";
import { positionToColor, vgColor, ListingBadge, LoanBadge, formatMln, renewEligibility, buybackReason, rosterStyleLabel } from "./helpers";
import { Cell, PlayerRow, RequestUpdateButtons, VetrinaButton, ReleaseButton, PromoteButton, DemoteButton } from "./rosterRows";
import { AdminRosterEditor, LegacyWageEditor } from "./admin";
import { RenewModal } from "./modals";

export function RosterSection({ title, subtitle, players, accent, testId, showLoanedTo = false, note, canMakeOffer = false, onMakeOffer, isAdmin = false, onResolveLoan, teamId, onLegacyWageSaved, canRenew = false, onRenewed, marketOpen = false, buyerTeamId = null, currentSeason = null, canListVetrina = false, canRequestUpdate = false, pendingUpdates = {}, onRequested }) {
    const [sortBy, setSortBy] = useState("default");
    const [sortDir, setSortDir] = useState("asc");

    const toggleSort = (key) => {
        if (sortBy === key) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortBy(key);
            // sensible default direction per column
            setSortDir(["vg", "market_value", "new_wage", "old_wage", "age"].includes(key) ? "desc" : "asc");
        }
    };

    const sorted = useMemo(() => {
        const dir = sortDir === "asc" ? 1 : -1;
        // Ordine tattico (portieri → attaccanti) per la colonna "position" e per il default.
        const POSITION_ORDER = ["GK","RB","CB","LB","DMF","CMF","LMF","RMF","AMF","LWF","RWF","SS","CF"];
        const POSITION_IDX = Object.fromEntries(POSITION_ORDER.map((p, i) => [p, i]));
        // DEFAULT: ordine tattico per ruolo, VG decrescente a parità di ruolo.
        if (sortBy === "default") {
            return [...players].sort((a, b) => {
                const ia = POSITION_IDX[a.position] ?? 99;
                const ib = POSITION_IDX[b.position] ?? 99;
                if (ia !== ib) return ia - ib;
                const va = a.vg ?? a.overall ?? -Infinity;
                const vb = b.vg ?? b.overall ?? -Infinity;
                return vb - va;
            });
        }
        const getVal = (p) => {
            switch (sortBy) {
                case "position": return POSITION_IDX[p.position] ?? 99;
                case "name": return (p.name || "").toLowerCase();
                case "loaned_to": return (p.loaned_to_team_name || "").toLowerCase();
                case "age": return p.age ?? -Infinity;
                case "vg": return p.vg ?? p.overall ?? -Infinity;
                case "market_value": return p.market_value ?? -Infinity;
                case "old_wage": return p.current_wage ?? -Infinity;
                case "new_wage": return p.new_rule_wage ?? p.computed_wage ?? p.applied_wage ?? -Infinity;
                case "years": return p.contract_years ?? -Infinity;
                case "status": return (p.status_label || p.status || "").toLowerCase();
                default: return 0;
            }
        };
        return [...players].sort((a, b) => {
            const va = getVal(a), vb = getVal(b);
            if (va < vb) return -1 * dir;
            if (va > vb) return 1 * dir;
            return 0;
        });
    }, [players, sortBy, sortDir]);

    const [openId, setOpenId] = useState(null);

    return (
        <section className="mb-10" data-testid={testId}>
            <div className="flex items-end justify-between border-b border-t22252a pb-3 mb-4">
                <h2 className="op-heading text-2xl" style={{ color: accent }}>{title}</h2>
                <div className="op-mono text-xs text-t8f95a3">{subtitle}</div>
            </div>
            {note && (
                <p className="op-mono text-xs text-t8f95a3 mb-3 leading-relaxed border-l-2 border-t8f95a3 pl-3">
                    {note}
                </p>
            )}

            {/* Desktop: table */}
            <div className="overflow-x-auto op-card hidden md:block">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-t8f95a3 border-b border-t22252a">
                            <SortableTh label="Pos" col="position" align="left" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                            <SortableTh label="Giocatore" col="name" align="left" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                            {showLoanedTo && (
                                <SortableTh label="A" col="loaned_to" align="left" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                            )}
                            <SortableTh label="Età" col="age" align="center" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                            <SortableTh label="VG" col="vg" align="center" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                            <SortableTh label="Valore TM" col="market_value" align="right" hideOnMd sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                            <SortableTh label="Legacy" col="old_wage" align="right" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                            <SortableTh label="Stipendio" col="new_wage" align="right" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                            <SortableTh label="Anni" col="years" align="center" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                            <SortableTh label="Status" col="status" align="left" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                            {canMakeOffer && <th className="py-2 px-3" />}
                            {isAdmin && onResolveLoan && <th className="py-2 px-3" />}
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((p, i) => (
                            <PlayerRow key={`${p.legacy_player_id || p.pes_id}-${i}`} p={p} showLoanedTo={showLoanedTo} canMakeOffer={canMakeOffer} onMakeOffer={onMakeOffer} isAdmin={isAdmin} onResolveLoan={onResolveLoan} teamId={teamId} onLegacyWageSaved={onLegacyWageSaved} canRenew={canRenew} onRenewed={onRenewed} marketOpen={marketOpen} buyerTeamId={buyerTeamId} currentSeason={currentSeason} canListVetrina={canListVetrina} canRequestUpdate={canRequestUpdate} pendingUpdates={pendingUpdates} onRequested={onRequested} />
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Mobile: lista a fisarmonica (riga compatta, apri per i dettagli) */}
            <div className="md:hidden space-y-2">
                {sorted.map((p, i) => {
                    const pid = p.entry_id || p.pes_id || p.legacy_player_id || i;
                    return (
                        <PlayerMobileCard key={`m-${p.legacy_player_id || p.pes_id}-${i}`} p={p} open={openId === pid} onToggle={() => setOpenId(openId === pid ? null : pid)} showLoanedTo={showLoanedTo} canMakeOffer={canMakeOffer} onMakeOffer={onMakeOffer} isAdmin={isAdmin} onResolveLoan={onResolveLoan} teamId={teamId} onLegacyWageSaved={onLegacyWageSaved} canRenew={canRenew} onRenewed={onRenewed} marketOpen={marketOpen} buyerTeamId={buyerTeamId} currentSeason={currentSeason} canListVetrina={canListVetrina} canRequestUpdate={canRequestUpdate} pendingUpdates={pendingUpdates} onRequested={onRequested} />
                    );
                })}
            </div>
        </section>
    );
}

export function SortableTh({ label, col, align, hideOnMd, sortBy, sortDir, onClick }) {
    const active = sortBy === col;
    const arrow = !active ? "↕" : sortDir === "asc" ? "↑" : "↓";
    const alignClass = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
    return (
        <th
            className={`${alignClass} py-2 px-3 op-label text-[10px] cursor-pointer select-none hover:text-tffffff transition-colors ${hideOnMd ? "hidden lg:table-cell" : ""}`}
            onClick={() => onClick(col)}
            style={{ color: active ? "rgb(var(--tc-ffffff))" : undefined }}
            data-testid={`sort-${col}`}
        >
            {label} <span className="op-mono text-[9px] opacity-70">{arrow}</span>
        </th>
    );
}

export function PlayerMobileCard({ p, open = false, onToggle, showLoanedTo, canMakeOffer = false, onMakeOffer, isAdmin = false, onResolveLoan, teamId, onLegacyWageSaved, canRenew = false, onRenewed, marketOpen = false, buyerTeamId = null, currentSeason = null, canListVetrina = false, canRequestUpdate = false, pendingUpdates = {}, onRequested }) {
    const [showRenew, setShowRenew] = useState(false);
    const positionColor = positionToColor(p.position);
    const noPes = !p.has_pes_id || p.pesdb_missing === true;
    const noPesReason = !p.has_pes_id ? "Mai sincronizzato con PES DB" : "Non più presente su PESDB (Authentic)";
    return (
        <div className="op-card overflow-hidden" data-testid={`player-mobile-${p.legacy_player_id || p.pes_id}`}>
            {/* Riga compatta — tocca per aprire/chiudere */}
            <button
                type="button"
                onClick={onToggle}
                className="w-full flex items-center gap-2.5 p-2.5 text-left"
                data-testid={`player-mobile-toggle-${p.entry_id || p.pes_id}`}
            >
                <PlayerAvatar tmImage={p.tm_image} pesImage={p.pes_image_url} name={p.name} size={40} />
                <span
                    className="op-mono text-[9px] font-bold px-1.5 py-0.5 rounded-sm leading-none shrink-0"
                    style={{ background: withAlpha(positionColor, "22"), color: positionColor }}
                >
                    {trPositionShort(p.position) || "—"}
                </span>
                <div className="flex-1 min-w-0">
                    <div className="text-tffffff text-sm font-medium truncate">{p.name || `#${p.legacy_player_id}`}</div>
                    <div className="op-mono text-[10px] text-t8f95a3 truncate">
                        {(() => { const st = rosterStyleLabel(p); return st ? `${st} · ` : ""; })()}{p.age ?? "—"} anni
                    </div>
                </div>
                {(p.listing_type || p.is_on_loan || p.is_loaned_out) && (
                    <span
                        className="w-1.5 h-1.5 rounded-full shrink-0"
                        style={{ background: p.is_on_loan || p.is_loaned_out ? "rgb(var(--tc-ff9d2b))" : (p.listing_type === "loan" ? "rgb(var(--tc-ff7a1a))" : "rgb(var(--tc-7ce38b))") }}
                        title={p.is_on_loan || p.is_loaned_out ? "In prestito" : (p.listing_type === "loan" ? "In prestito (vetrina)" : "In vendita")}
                    />
                )}
                {(pendingUpdates[p.entry_id] || []).length > 0 && (
                    <span
                        className="w-1.5 h-1.5 rounded-full shrink-0"
                        style={{ background: "rgb(var(--tc-9d7cff))" }}
                        title="Aggiornamento TM/PESDB richiesto"
                        data-testid={`update-pending-dot-${p.entry_id}`}
                    />
                )}
                <div className="text-right shrink-0">
                    <div className="op-mono font-bold text-lg leading-none" style={{ color: vgColor(p.vg) }}>{p.vg ?? "—"}</div>
                    <div className="op-label text-[8px]">VG</div>
                </div>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`w-4 h-4 text-t5c6370 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}>
                    <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>

            {open && (
            <div className="px-3 pb-3 border-t border-t1a1d24" data-testid={`player-mobile-panel-${p.entry_id || p.pes_id}`}>
                {/* Badge + bandiera + stile di gioco + link alla scheda */}
                <div className="flex items-center flex-wrap gap-1.5 pt-2.5">
                    {noPes && (
                        <span className="op-mono text-[8px] text-tff6b00 border border-tff6b00/40 px-1 py-0 rounded-sm uppercase" title={noPesReason}>
                            no PES
                        </span>
                    )}
                    {p.nationality && flagUrl(p.nationality) ? (
                        <img src={flagUrl(p.nationality)} alt={p.nationality} title={p.nationality} className="h-3 w-auto inline-block opacity-90" loading="lazy" />
                    ) : p.nationality ? (
                        <span className="op-mono text-[9px] text-t8f95a3">{p.nationality}</span>
                    ) : null}
                    <ListingBadge type={p.listing_type} />
                    <LoanBadge p={p} />
                </div>
                {(p.pes_id ?? p.entry_id) && (
                    <Link to={`/giocatori/${p.pes_id ?? p.entry_id}`} className="op-mono text-[11px] text-t2bb6ff hover:underline inline-block mt-1.5">
                        ↗ Apri scheda giocatore
                    </Link>
                )}

            {/* Loaned-to row (only when applicable) */}
            {showLoanedTo && p.playing_team_name && (
                <div className="mb-2 op-mono text-[10px] text-t8f95a3 uppercase">
                    Prestato a: <span className="text-tffffff normal-case">{p.playing_team_name}</span>
                    {p.loan_kind === "loan_option" && <span className="ml-1 text-tff9d2b">· diritto</span>}
                    {p.loan_kind === "loan" && <span className="ml-1 text-t8f95a3">· secco</span>}
                </div>
            )}

            {/* Stats grid */}
            <div className="grid grid-cols-4 gap-2 text-center border-t border-t1a1d24 pt-2.5">
                <Cell label="Età" value={p.age ?? "—"} />
                <Cell label="Anni cnt." value={p.contract_years ?? "—"} />
                <Cell label="Valore TM" value={formatMln(p.market_value)} color="rgb(var(--tc-00bfff))" />
                <Cell
                    label="Legacy"
                    valueNode={
                        <LegacyWageEditor
                            entryId={p.entry_id}
                            teamId={teamId}
                            currentWage={p.current_wage}
                            applied={p.applied_wage_source === "legacy"}
                            isAdmin={isAdmin}
                            onSaved={onLegacyWageSaved}
                        />
                    }
                />
            </div>
            <div className="grid grid-cols-2 gap-2 text-center mt-2">
                <Cell
                    label="Stipendio"
                    valueNode={(() => {
                        const frozen = p.applied_wage_source === "renewed" || p.applied_wage_source === "locked";
                        const mainWage = frozen ? p.applied_wage : (p.new_rule_wage ?? p.computed_wage);
                        const showProj = p.purchase_wage != null && p.purchase_wage !== mainWage;
                        return (
                        <div className="leading-tight">
                            <div className="text-xl font-bold"
                                 style={{ color: (p.applied_wage_source === "new" || frozen) ? "rgb(var(--tc-10b981))" : "rgb(var(--tc-ffc93c))" }}
                                 title={p.applied_wage_source === "renewed" ? "🟢 Stipendio rinnovato (attivo)" : (p.applied_wage_source === "locked" ? "🟢 Stipendio del contratto (non cambia col VG)" : (p.applied_wage_source === "new" ? "🟢 Pagherai questo (formula nuova)" : "🟡 Formula nuova: il legacy è più basso quindi paghi il legacy"))}>
                                {formatMln(mainWage)}
                            </div>
                            {p.applied_wage_source === "renewed" && (
                                <div className="op-mono text-[10px] text-t7ce38b mt-0.5" title="Stipendio bloccato dal rinnovo del contratto">(rinnovato)</div>
                            )}
                            {showProj && (
                                <div className="op-mono text-[10px] text-tff7a1a mt-0.5" title="Stipendio se acquistato o rinnovato oggi (nostra formula nuova, sul VG attuale)">
                                    ({formatMln(p.purchase_wage)} se acq./rinnovo)
                                </div>
                            )}
                        </div>
                        );
                    })()}
                />
                <Cell label="Status" valueNode={(p.statuses || []).length === 0 ? (
                    <span className="op-mono text-[10px] text-t5c6370">—</span>
                ) : (
                    <div className="flex flex-wrap gap-1 justify-center">
                        {(p.statuses || []).map((s, i) => (
                            <span key={i} className={`op-mono text-[9px] px-1.5 py-0.5 border uppercase leading-none ${s === "SVI" ? "border-tffb020/50 text-tffb020" : "border-t22252a text-tc9ccd3"}`} title={s === "SVI" ? "Preso da svincolato in questa stagione: non svincolabile, solo vendibile" : undefined}>
                                {s}
                            </span>
                        ))}
                    </div>
                )} />
            </div>
            {canRenew && p.entry_id && !p.is_loaned_out && !p.is_on_loan && (() => {
                const renew = renewEligibility(p);
                return (
                    <div className="mt-3 pt-2 border-t border-t1a1d24">
                        <button
                            className={`w-full op-mono text-xs uppercase tracking-wider py-2 border transition-colors ${renew.allowed ? "border-t7ce38b/40 text-t7ce38b hover:bg-t7ce38b/10" : "border-t3a3d44 text-t5c6370 cursor-not-allowed"}`}
                            onClick={() => renew.allowed && setShowRenew(true)}
                            disabled={!renew.allowed}
                            title={renew.reason}
                            data-testid={`renew-btn-mobile-${p.entry_id}`}
                        >
                            Rinnova contratto
                        </button>
                        {!renew.allowed && (
                            <div className="op-mono text-[9px] text-t5c6370 mt-1 text-center leading-tight">
                                {renew.reason}
                            </div>
                        )}
                        <ReleaseButton p={p} teamId={teamId} onReleased={onRenewed} marketOpen={marketOpen} currentSeason={currentSeason} />
                        {p.is_ugly && (
                            <PromoteButton p={p} teamId={teamId} onPromoted={onRenewed} marketOpen={marketOpen} />
                        )}
                        {!p.is_ugly && (
                            <DemoteButton p={p} teamId={teamId} onDemoted={onRenewed} marketOpen={marketOpen} />
                        )}
                    </div>
                );
            })()}
            {canRenew && p.entry_id && !p.is_loaned_out && p.is_on_loan && p.is_ugly && (
                <div className="mt-2">
                    <PromoteButton p={p} teamId={teamId} onPromoted={onRenewed} marketOpen={marketOpen} />
                </div>
            )}
            {canRenew && p.entry_id && !p.is_loaned_out && p.is_on_loan && !p.is_ugly && (
                <div className="mt-2">
                    <DemoteButton p={p} teamId={teamId} onDemoted={onRenewed} marketOpen={marketOpen} />
                </div>
            )}
            {canListVetrina && p.entry_id && !p.is_loaned_out && !p.is_on_loan && (
                <div className="mt-2">
                    <VetrinaButton p={p} onChanged={onRenewed} />
                </div>
            )}
            {canRequestUpdate && p.entry_id && (
                <div className="mt-3 pt-2 border-t border-t1a1d24">
                    <RequestUpdateButtons p={p} teamId={teamId} pendingKinds={pendingUpdates[p.entry_id] || []} onRequested={onRequested} />
                </div>
            )}
            {showRenew && (
                <RenewModal player={p} teamId={teamId} onClose={() => setShowRenew(false)} onDone={() => { setShowRenew(false); onRenewed?.(); }} />
            )}
            {canMakeOffer && p.entry_id && !p.is_loaned_out && (() => {
                const loanBlock = p.is_on_loan
                    ? "In prestito: non cedibile finché il riscatto non è risolto (fine stagione)"
                    : null;
                const bbReason = loanBlock ? null : buybackReason(p, buyerTeamId, currentSeason);
                return (
                    <div className="mt-3 pt-2 border-t border-t1a1d24">
                        <button
                            className={`w-full op-mono text-xs uppercase tracking-wider py-2 border transition-colors ${loanBlock ? "border-t3a3d44 text-t5c6370 cursor-not-allowed" : "border-t2bb6ff/40 text-t2bb6ff hover:bg-t2bb6ff/10"}`}
                            onClick={() => !loanBlock && onMakeOffer?.(p)}
                            disabled={!!loanBlock}
                            title={loanBlock || undefined}
                            data-testid={`offer-btn-mobile-${p.entry_id}`}
                        >
                            {loanBlock ? "Bloccato" : "Fai offerta"}
                        </button>
                        {(loanBlock || bbReason) && (
                            <div className="op-mono text-[9px] text-t5c6370 mt-1 text-center leading-tight">
                                {loanBlock || `${bbReason} (puoi comunque fare un'offerta di PRESTITO)`}
                            </div>
                        )}
                    </div>
                );
            })()}
            {isAdmin && onResolveLoan && p.entry_id && p.is_loaned_out && !p.is_away_loan && (
                <div className="mt-3 pt-2 border-t border-t1a1d24">
                    <button
                        className="w-full op-mono text-xs uppercase tracking-wider py-2 border border-tff6b00/50 text-tff6b00 hover:bg-tff6b00/10 transition-colors"
                        onClick={() => onResolveLoan?.(p)}
                        data-testid={`resolve-btn-mobile-${p.entry_id}`}
                    >
                        Risolvi prestito
                    </button>
                </div>
            )}
            {isAdmin && p.entry_id && !p.is_away_loan && (
                <div className="mt-3 pt-2 border-t border-t1a1d24">
                    <AdminRosterEditor p={p} teamId={teamId} isAdmin={isAdmin} onChanged={onRenewed} />
                </div>
            )}
            </div>
            )}
        </div>
    );
}

